import { KostDataImportWizardState } from '@bindoc-projects/kost-data-import-lib/logic';
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import {
  BD_HELP_BUTTON_OPTIONS_TOOLBAR,
  BdHelpButtonComponent,
  BdHelpButtonOptions,
} from '@bindoc-projects/help-lib/ui';
import { StepperModule } from 'primeng/stepper';
import {
  KOST_DATA_IMPORT_TARGET_CONFIG_VIRTUAL_CLINICS_SELECT_OPTIONS_DEFAULT,
  KostDataImportTargetConfig,
} from '@bindoc-projects/kost-data-import-lib/ui';
import {
  KostDataImportNewTargetComponent,
  KostDataImportWizardNavComponent,
} from '@bindoc-projects/kost-data-import-lib/ui';
import { KostDataImportDeNewTermsAndConditionsDialogComponent } from './target/tos/terms-and-conditions-dialog.component';
import { BdLoaderComponent } from '@bindoc-projects/ui-lib/feedback/ui';
import { BdLoaderState } from '@bindoc-projects/platform-lib/data';
import { BdMessageEmptyComponent } from '@bindoc-projects/ui-lib/feedback/ui';
import {
  KOST_DATA_IMPORT_PROCESS_DATA_DE_DEFAULT,
  KostDataImportProcessDataDe,
} from './import-process';
import { KostDataImportDeNewParametersDataComponent } from './parameters/data/data.component';
import { KostDataImportDeNewParametersAnonymizationComponent } from './parameters/anonymization/anonymization.component';
import { ReplaySubject } from 'rxjs';
import { KostDataImportDeNewDatasetInputComponent } from './dataset/input/input.component';
import { DependenciesService } from './dependencies.service';
import { KostDataImportDeNewUploadComponent } from './upload/upload.component';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import { Toolbar } from 'primeng/toolbar';

@Component({
  template: `
    <p-toolbar>
      <ng-template #start>
        <h1 class="flex-1">Neuer Struktur- und Leistungsdaten Import</h1>
      </ng-template>
      <ng-template #end>
        <div class="flex items-center gap-2">
          <bd-help-button
            key="ays_virtual_clinic_import"
            [options]="toolbarOptions"
          >
          </bd-help-button>
        </div>
      </ng-template>
    </p-toolbar>

    <div class="bd-container">
      <bd-loader
        [loadItem]="loading$"
        [successTemplate]="formTpl"
        [errorTemplate]="errorTpl"
      ></bd-loader>
    </div>

    <ng-template #errorTpl>
      <div class="flex flex-col items-center gap-4 p-8">
        <bd-message-empty
          icon="pi pi-times-circle"
          message="Die erforderlichen Daten konnten nicht geladen werden."
        ></bd-message-empty>
        <p class="text-sm text-muted-color">{{ errorMessage }}</p>
      </div>
    </ng-template>

    <ng-template #formTpl>
      <p-stepper [linear]="true" [value]="currentStep.index">
        <p-step-list>
          <p-step [value]="1">Datenziel</p-step>
          <p-step [value]="2">Parameter Datensatz</p-step>
          <p-step [value]="3">Parameter Anonymisierung</p-step>
          <p-step [value]="4">Dateiauswahl</p-step>
          <p-step [value]="5">Übertragung</p-step>
        </p-step-list>
        <p-step-panels>
          <p-step-panel [value]="1">
            <ng-template #content>
              <kost-data-import-new-target
                [config]="targetConfig"
                [processData]="processData"
                (update)="onStepUpdate($event)"
              >
              </kost-data-import-new-target>
              <kost-data-import-wizard-nav
                [back]="false"
                [nextAllowed]="currentStep.allowNext"
                nextDisabledReason="Wählen Sie eine virtuelle Klinik aus und akzeptieren Sie die Nutzungsbedingungen."
                (next)="activateStep(2)"
              />
            </ng-template>
          </p-step-panel>
          <p-step-panel [value]="2">
            <ng-template #content>
              <kost-data-import-de-new-parameters-data
                [processData]="processData"
                (update)="onStepUpdate($event)"
              >
              </kost-data-import-de-new-parameters-data>
              <kost-data-import-wizard-nav
                [nextAllowed]="currentStep.allowNext"
                nextDisabledReason="Wählen Sie die Fachabteilungs-Zuordnung und den Landesbasisfallwert aus."
                (previous)="activateStep(1)"
                (next)="activateStep(3)"
              />
            </ng-template>
          </p-step-panel>
          <p-step-panel [value]="3">
            <ng-template #content>
              <kost-data-import-de-new-parameters-anonymization
                [processData]="processData"
                (update)="onStepUpdate($event)"
              >
              </kost-data-import-de-new-parameters-anonymization>
              <kost-data-import-wizard-nav
                [nextAllowed]="currentStep.allowNext"
                nextDisabledReason="Geben Sie einen Salt für die Anonymisierung ein."
                (previous)="activateStep(2)"
                (next)="activateStep(4)"
              />
            </ng-template>
          </p-step-panel>
          <p-step-panel [value]="4">
            <ng-template #content>
              <kost-data-import-de-new-dataset-input
                [processData]="processData"
                (update)="onStepUpdate($event)"
              >
              </kost-data-import-de-new-dataset-input>
              <kost-data-import-wizard-nav
                [nextAllowed]="currentStep.allowNext"
                nextDisabledReason="Wählen Sie eine Datei aus und warten Sie, bis sie fehlerfrei verarbeitet ist."
                (previous)="activateStep(3)"
                (next)="activateStep(5)"
              />
            </ng-template>
          </p-step-panel>
          <p-step-panel [value]="5">
            <ng-template #content>
              <kost-data-import-de-new-upload [processData]="processData">
              </kost-data-import-de-new-upload>
            </ng-template>
          </p-step-panel>
        </p-step-panels>
      </p-stepper>
    </ng-template>
  `,

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    Toolbar,
    StepperModule,
    BdHelpButtonComponent,
    KostDataImportNewTargetComponent,
    KostDataImportWizardNavComponent,
    KostDataImportDeNewParametersDataComponent,
    KostDataImportDeNewParametersAnonymizationComponent,
    BdLoaderComponent,
    BdMessageEmptyComponent,
    KostDataImportDeNewDatasetInputComponent,
    KostDataImportDeNewUploadComponent,
  ],
})
export class KostDataImportDeNewComponent extends KostDataImportWizardState<KostDataImportProcessDataDe> {
  private readonly _dependenciesService = inject(DependenciesService);

  public readonly toolbarOptions: BdHelpButtonOptions =
    BD_HELP_BUTTON_OPTIONS_TOOLBAR;

  public readonly targetConfig: KostDataImportTargetConfig = {
    virtualClinicSelectOptions:
      KOST_DATA_IMPORT_TARGET_CONFIG_VIRTUAL_CLINICS_SELECT_OPTIONS_DEFAULT,
    termsAndConditions: {
      tosDate: new Date(2024, 0, 16),
      dialog: KostDataImportDeNewTermsAndConditionsDialogComponent,
    },
    resourceName: ShareableType.VIRTUAL_CLINIC,
  };

  public readonly loading$ = new ReplaySubject<BdLoaderState>(1);
  public errorMessage = '';

  constructor() {
    super(KOST_DATA_IMPORT_PROCESS_DATA_DE_DEFAULT);
    this.loading$.next(BdLoaderState.LOADING);

    this._dependenciesService.fetchDependencies().subscribe({
      next: (dependencies) => {
        this.processData.dependencies = dependencies;
        this.loading$.next(BdLoaderState.SUCCESS);
      },
      error: (err: Error) => {
        this.errorMessage = err.message;
        this.loading$.next(BdLoaderState.ERROR);
      },
    });
  }
}
